import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, CircularProgress, MenuItem, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  TablePagination, Chip, Button, Fade, Alert, Divider
} from '@mui/material';
import { 
  CalendarToday, 
  People, 
  FilterList, 
  Refresh, 
  Download
} from '@mui/icons-material';
import * as XLSX from 'xlsx'; // Added for Excel Export
import api from '../../api';

const MonthlyReportSection = () => {
  const [data, setData] = useState({ headers: [], matrix: [] });
  const [loading, setLoading] = useState(true);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Default Payroll Cycle Logic
  const currentDate = new Date();
  let defaultMonth = currentDate.getMonth() + 1;
  let defaultYear = currentDate.getFullYear();
  
  if (currentDate.getDate() >= 16) {
    defaultMonth += 1;
    if (defaultMonth > 12) {
      defaultMonth = 1;
      defaultYear += 1;
    }
  }

  const [month, setMonth] = useState(defaultMonth);
  const [year, setYear] = useState(defaultYear);
  const [department, setDepartment] = useState('ALL');
  
  const empId = localStorage.getItem('emp_id');

  // Fetch Departments on Mount
  useEffect(() => {
    api.get('/attendance/departments')
      .then(res => setDepartmentsList(res.data))
      .catch(() => setError('Failed to load departments'));
  }, []);

  // Fetch Matrix Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setPage(0);
      
      try {
        const res = await api.get(`/attendance/monthly-report/${empId}?month=${month}&year=${year}&department=${department}`);
        setData(res.data);
      } catch (err) {
        setError('Failed to load report data. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [empId, month, year, department]);

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // EXPORT LOGIC
  const handleExport = () => {
    if (!data || data.matrix.length === 0) return;

    // Flatten data for Excel
    const exportData = data.matrix.map(row => {
      const rowData = {
        "Emp ID": row.emp_id,
        "Employee Name": row.name,
        "Department": row.department
      };
      
      // Append each date as a column
      data.headers.forEach(h => {
        const dateHeader = `${h.day}-${h.month}`;
        rowData[dateHeader] = row.attendance[h.date_str] || '';
      });
      
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly_Attendance");
    XLSX.writeFile(workbook, `Monthly_Attendance_Report_${month}_${year}.xlsx`);
  };

  const getStatusStyle = (letter) => {
    const styles = {
      'P': { bg: '#e8f5e9', color: '#2e7d32', border: '#4caf50', label: 'Present' },
      'W': { bg: '#f3f4f6', color: '#6b7280', border: '#9ca3af', label: 'Week Off' },
      'H': { bg: '#fff3e0', color: '#e65100', border: '#ff9800', label: 'Half-Day' },
      'L': { bg: '#fce4ec', color: '#c2185b', border: '#e91e63', label: 'LOP' },
      'D': { bg: '#ffebee', color: '#c62828', border: '#f44336', label: 'DLOP' },
      'N': { bg: '#f3e5f5', color: '#6a1b9a', border: '#9c27b0', label: 'National Holiday' },
      'C': { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3', label: 'Comp Off' },
      'M': { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3', label: 'Medical Leave' },
      'A': { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3', label: 'Annual Leave' },
      'E': { bg: '#e3f2fd', color: '#1565c0', border: '#2196f3', label: 'Emergency' },
    };
    return styles[letter] || { bg: 'transparent', color: '#9e9e9e', border: '#9e9e9e', label: letter };
  };

  const LegendItem = ({ letter }) => {
    const style = getStatusStyle(letter);
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5,
        px: 0.5
      }}>
        <Box sx={{ 
          width: 22, 
          height: 22, 
          borderRadius: 1, 
          bgcolor: style.bg, 
          color: style.color, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          fontSize: '0.65rem', 
          fontWeight: 700,
          border: `1.5px solid ${style.border}40`
        }}>
          {letter}
        </Box>
      </Box>
    );
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Fade in timeout={500}>
      <Box sx={{ 
        width: '100%', 
        maxWidth: 'calc(100vw - 340px)', 
        minWidth: 0, 
        overflow: 'hidden',
        p: { xs: 1, sm: 2 }
      }}>
        
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2
        }}>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 0.5
              }}
            >
              <CalendarToday sx={{ color: 'primary.main', fontSize: 28 }} />
              Monthly Attendance Report
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1 }}>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                {monthNames[month - 1]} {year}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ height: 16 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <People sx={{ color: 'primary.main', fontSize: 16 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {data.matrix.length} employees
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <Button 
              variant="outlined" 
              size="small"
              startIcon={<Refresh />}
              onClick={() => {
                setLoading(true);
                const fetchData = async () => {
                  try {
                    const res = await api.get(`/attendance/monthly-report/${empId}?month=${month}&year=${year}&department=${department}`);
                    setData(res.data);
                  } catch (err) {
                    setError('Failed to refresh report');
                  } finally {
                    setLoading(false);
                  }
                };
                fetchData();
              }}
            >
              Refresh
            </Button>
            <Button 
              variant="contained" 
              size="small"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={data.matrix.length === 0}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: 'background.subtle'
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Filters
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              flexWrap: 'wrap',
              flex: 1
            }}>
              <TextField 
                select 
                size="small" 
                value={department} 
                onChange={(e) => setDepartment(e.target.value)}
                sx={{ 
                  minWidth: 180,
                  flex: { xs: 1, sm: 0 },
                  bgcolor: 'background.paper'
                }}
              >
                <MenuItem value="ALL">All Departments</MenuItem>
                {departmentsList.map(dept => (
                  <MenuItem key={dept.id} value={dept.name}>{dept.name}</MenuItem>
                ))}
              </TextField>
              
              <TextField 
                select 
                size="small" 
                value={month} 
                onChange={(e) => setMonth(Number(e.target.value))}
                sx={{ 
                  minWidth: 130,
                  flex: { xs: 1, sm: 0 },
                  bgcolor: 'background.paper'
                }}
              >
                {monthNames.map((name, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{name}</MenuItem>
                ))}
              </TextField>
              
              <TextField 
                select 
                size="small" 
                value={year} 
                onChange={(e) => setYear(Number(e.target.value))}
                sx={{ 
                  minWidth: 100,
                  flex: { xs: 1, sm: 0 },
                  bgcolor: 'background.paper'
                }}
              >
                {[year - 1, year, year + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        </Paper>

        {/* Main Table */}
        <Paper 
          elevation={0}
          sx={{ 
            overflow: 'hidden'
          }}
        >
          {/* Legend */}
          <Box sx={{ 
            p: 1.5,
            bgcolor: 'background.subtle',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', mr: 0.5 }}>
              LEGEND:
            </Typography>
            {['P', 'W', 'H', 'L', 'D', 'N', 'C', 'M'].map((letter) => (
              <LegendItem key={letter} letter={letter} />
            ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              p: 8,
              gap: 2
            }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary">
                Loading attendance data...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%', position: 'relative' }}>
              <TableContainer sx={{ 
                maxHeight: '65vh', 
                width: '100%', 
                overflowX: 'auto'
              }}>
                <Table 
                  stickyHeader 
                  size="small" 
                  sx={{ 
                    width: 'max-content', 
                    minWidth: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {/* Employee column - Sticky with proper z-index */}
                      <TableCell 
                        sx={{ 
                          position: 'sticky',
                          left: 0,
                          zIndex: 20,
                          minWidth: 160,
                          maxWidth: 160,
                          width: 160,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRight: '2px solid',
                          borderColor: 'primary.dark',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: -2,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            bgcolor: 'primary.main',
                            zIndex: 1
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                          EMPLOYEE
                        </Typography>
                      </TableCell>
                      
                      {/* Department column - Sticky with proper z-index */}
                      <TableCell 
                        sx={{ 
                          position: 'sticky',
                          left: 160,
                          zIndex: 20,
                          minWidth: 130,
                          maxWidth: 130,
                          width: 130,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRight: '2px solid',
                          borderColor: 'primary.dark',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: -2,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            bgcolor: 'primary.main',
                            zIndex: 1
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                          DEPARTMENT
                        </Typography>
                      </TableCell>
                      
                      {/* Date columns */}
                      {data.headers.map((h, i) => (
                        <TableCell 
                          key={i} 
                          align="center" 
                          sx={{ 
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            padding: '6px 4px',
                            minWidth: 42,
                            width: 42,
                            borderRight: i < data.headers.length - 1 ? '1px solid' : 'none',
                            borderColor: 'primary.dark'
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.85rem', 
                              fontWeight: 700, 
                              color: 'primary.contrastText' 
                            }}>
                              {h.day}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontSize: '0.6rem', 
                              color: 'rgba(255,255,255,0.7)',
                              marginTop: 0.5
                            }}>
                              {h.month}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.matrix.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={data.headers.length + 2} align="center" sx={{ py: 6 }}>
                          <Typography variant="body1" color="text.secondary">
                            No subordinates found for the selected filters.
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Try adjusting your department or date range.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.matrix
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((row, idx) => (
                          <TableRow 
                            key={idx} 
                            hover
                          >
                            {/* Employee cell - Sticky with background color */}
                            <TableCell 
                              sx={{ 
                                position: 'sticky',
                                left: 0,
                                zIndex: 10,
                                bgcolor: 'background.paper',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderRight: '2px solid',
                                borderColor: 'divider',
                                py: 1.5,
                                color: 'text.primary',
                                minWidth: 160,
                                maxWidth: 160,
                                width: 160,
                                '&:hover': { 
                                  bgcolor: 'action.hover' 
                                }
                              }}
                            >
                              {row.name}
                            </TableCell>
                            
                            {/* Department cell - Sticky with background color */}
                            <TableCell 
                              sx={{ 
                                position: 'sticky',
                                left: 160,
                                zIndex: 10,
                                bgcolor: 'background.paper',
                                fontSize: '0.8rem',
                                borderRight: '2px solid',
                                borderColor: 'divider',
                                py: 1.5,
                                minWidth: 130,
                                maxWidth: 130,
                                width: 130,
                                '&:hover': { 
                                  bgcolor: 'action.hover' 
                                }
                              }}
                            >
                              <Chip 
                                label={row.department} 
                                size="small"
                                color="primary"
                                variant="filled"
                              />
                            </TableCell>
                            
                            {/* Date columns data */}
                            {data.headers.map((h, i) => {
                              const letter = row.attendance[h.date_str];
                              const style = getStatusStyle(letter);
                              return (
                                <TableCell 
                                  key={i} 
                                  align="center" 
                                  sx={{ 
                                    padding: '4px 2px',
                                    borderRight: i < data.headers.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                    minWidth: 42,
                                    width: 42
                                  }}
                                >
                                  {letter ? (
                                    <Box sx={{
                                      width: 26,
                                      height: 26,
                                      margin: 'auto',
                                      borderRadius: 1,
                                      bgcolor: style.bg,
                                      color: style.color,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      border: `1.5px solid ${style.border}30`
                                    }}>
                                      {letter}
                                    </Box>
                                  ) : (
                                    <Box sx={{ width: 26, height: 26 }} />
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.subtle'
              }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Showing {data.matrix.length > 0 ? page * rowsPerPage + 1 : 0} to{' '}
                  {Math.min((page + 1) * rowsPerPage, data.matrix.length)} of{' '}
                  {data.matrix.length} employees
                </Typography>
                
                <TablePagination
                  component="div"
                  count={data.matrix.length}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{
                    border: 'none'
                  }}
                />
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default MonthlyReportSection;